import { useCallback, useEffect, useRef, useState } from "react";
import { IconPauza, IconPlay } from "../icons/index.js";
import { Button } from "./Button.js";

/**
 * WY-3: odtwarzacz nagrania. Wlasne kontrolki DS, NIGDY goly <audio controls> -
 * natywny pasek ma swoje kolory, swoj krój i swoje promienie, czyli wyglada jak
 * cialo obce w karcie. Pilnuje tego straznik tests/static/noNativeDialogs.test.ts.
 *
 * Element <audio> zostaje, ale bez atrybutu controls - jest silnikiem, nie interfejsem.
 * Pasek postepu odtwarzania idzie wzorcem PR-2 (tor --surface-sunken, wypelnienie
 * --primary) z przeciagalnym znacznikiem.
 */

const PREDKOSCI = [1, 1.25, 1.5, 2] as const;

function czas(sekundy: number): string {
  if (!Number.isFinite(sekundy) || sekundy < 0) return "0:00";
  const m = Math.floor(sekundy / 60);
  const s = Math.floor(sekundy % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function AudioPlayer({ zrodlo, etykieta = "Nagranie rozmowy" }: { zrodlo: string; etykieta?: string }): React.JSX.Element {
  const audioRef = useRef<HTMLAudioElement>(null);
  const torRef = useRef<HTMLDivElement>(null);
  const [gra, setGra] = useState(false);
  const [pozycja, setPozycja] = useState(0);
  const [dlugosc, setDlugosc] = useState(0);
  const [predkosc, setPredkosc] = useState<number>(PREDKOSCI[0]);

  const procent = dlugosc > 0 ? Math.min(100, (pozycja / dlugosc) * 100) : 0;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const naCzas = (): void => setPozycja(el.currentTime);
    const naMetadane = (): void => setDlugosc(Number.isFinite(el.duration) ? el.duration : 0);
    const naKoniec = (): void => setGra(false);
    el.addEventListener("timeupdate", naCzas);
    el.addEventListener("loadedmetadata", naMetadane);
    el.addEventListener("ended", naKoniec);
    return () => {
      el.removeEventListener("timeupdate", naCzas);
      el.removeEventListener("loadedmetadata", naMetadane);
      el.removeEventListener("ended", naKoniec);
    };
  }, []);

  const przewin = useCallback(
    (sekundy: number) => {
      const el = audioRef.current;
      if (!el || dlugosc <= 0) return;
      const cel = Math.max(0, Math.min(dlugosc, sekundy));
      el.currentTime = cel;
      setPozycja(cel);
    },
    [dlugosc],
  );

  function przelacz(): void {
    const el = audioRef.current;
    if (!el) return;
    if (gra) {
      el.pause();
      setGra(false);
      return;
    }
    // play() zwraca obietnice, ktora odrzuca sie np. przy braku interakcji
    // uzytkownika. Nieobsluzona wywalilaby nieprzechwycony blad w konsoli.
    void el.play().then(
      () => setGra(true),
      () => setGra(false),
    );
  }

  function zEventu(clientX: number): void {
    const tor = torRef.current;
    if (!tor || dlugosc <= 0) return;
    const prostokat = tor.getBoundingClientRect();
    if (prostokat.width === 0) return;
    przewin(((clientX - prostokat.left) / prostokat.width) * dlugosc);
  }

  return (
    <div className="audio-player">
      {/* Bez atrybutu controls - interfejs rysujemy sami (WY-3). */}
      <audio ref={audioRef} src={zrodlo} preload="metadata" />

      <div className="audio-row">
        <Button
          wariant="secondary"
          tylkoIkona
          aria-label={gra ? "Wstrzymaj nagranie" : "Odtwórz nagranie"}
          onClick={przelacz}
        >
          {gra ? <IconPauza size={18} /> : <IconPlay size={18} />}
        </Button>

        <div
          className="progress-scrub"
          ref={torRef}
          role="slider"
          tabIndex={0}
          aria-label={`Postęp odtwarzania - ${etykieta}`}
          aria-valuemin={0}
          aria-valuemax={Math.round(dlugosc)}
          aria-valuenow={Math.round(pozycja)}
          aria-valuetext={`${czas(pozycja)} z ${czas(dlugosc)}`}
          onMouseDown={(e) => zEventu(e.clientX)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              e.preventDefault();
              przewin(pozycja + 5);
            } else if (e.key === "ArrowLeft") {
              e.preventDefault();
              przewin(pozycja - 5);
            } else if (e.key === "Home") {
              e.preventDefault();
              przewin(0);
            } else if (e.key === "End") {
              e.preventDefault();
              przewin(dlugosc);
            }
          }}
        >
          <div className="progress-scrub__fill" style={{ width: `${procent}%` }} />
          <div className="progress-scrub__thumb" style={{ left: `${procent}%` }} />
        </div>
      </div>

      <div className="audio-times">
        <span>{czas(pozycja)}</span>
        <span>{czas(dlugosc)}</span>
      </div>

      <div className="audio-actions">
        <Button
          wariant="secondary"
          onClick={() => {
            const nastepna = PREDKOSCI[(PREDKOSCI.indexOf(predkosc as (typeof PREDKOSCI)[number]) + 1) % PREDKOSCI.length];
            setPredkosc(nastepna);
            if (audioRef.current) audioRef.current.playbackRate = nastepna;
          }}
        >
          {`Prędkość ${String(predkosc).replace(".", ",")}x`}
        </Button>
      </div>
    </div>
  );
}
